import { useEffect, useRef, useState } from 'react';
import { useCurrentUser } from '@/api/auth';
import { counter, IDLE_LOCK_MS } from './counterMode';

/**
 * Cet écran est-il en lecture seule ?
 *
 * Un poste comptoir affiche les mêmes pages que le titulaire — c'est tout
 * l'intérêt, l'équipe voit le vrai planning et la vraie organisation — mais il
 * ne les modifie pas. Le serveur refuse de toute façon (liste blanche du mode
 * comptoir) ; ce drapeau sert à ne pas proposer des gestes qui finiraient en
 * message d'erreur.
 */
export function useCounterReadOnly(): boolean {
  const { data: user } = useCurrentUser();
  return user?.role === 'TEAM_PIN';
}

/**
 * Referme la session de gestion toute seule.
 *
 * Le titulaire déverrouille au comptoir, est appelé au téléphone, et s'en va.
 * Sans cette minuterie, la pharmacie entière hérite de ses droits jusqu'à
 * l'expiration du jeton. Tout geste (souris, clavier, tactile) repousse
 * l'échéance ; cinq minutes sans rien, et l'écran retourne à l'équipe.
 *
 * À monter une seule fois, dans le layout titulaire.
 */
export function useCounterIdleLock() {
  const [remaining, setRemaining] = useState(() => counter.remainingMs());
  // Le rechargement n'est pas instantané : sans ce garde, le tour de minuterie
  // suivant en déclencherait un second pendant que le premier est en route.
  const leaving = useRef(false);

  useEffect(() => {
    if (!counter.isPost()) return;

    const touch = () => counter.touch();
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, touch, { passive: true }));

    const timer = setInterval(() => {
      setRemaining(counter.remainingMs());
      // `unlockedToken()` périme la session au passage : il suffit de constater
      // qu'elle a disparu pour rendre l'écran à l'équipe.
      //
      // On recharge, on ne se contente pas de vider le cache : c'est le jeton,
      // le rôle et le cadre qui changent d'un coup (même raison que
      // `reloadInto` dans `api/auth.ts`). Et sur un poste que le titulaire vient
      // de quitter, ne rien laisser de sa session en mémoire est le minimum.
      if (!counter.isUnlocked() && !leaving.current) {
        leaving.current = true;
        window.location.assign('/planning');
      }
    }, 15_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, touch));
      clearInterval(timer);
    };
  }, []);

  return {
    /** Poste comptoir dont la gestion est ouverte (donc verrouillable). */
    onCounterPost: counter.isPost(),
    remainingMs: remaining,
    idleLockMs: IDLE_LOCK_MS,
  };
}
